import { nothing } from 'lit-html';
import { html, svg } from 'lit-element';
import { KatLitElement, register } from '../../shared/base';
import { rangeMap } from '../../shared/utils';

import baseStyles from '../../shared/base/base.lit.scss';
import styles from './statustracker.lit.scss';

const LINE_LENGTH = 20;
const STEP_DOT_RADIUS = 2.5;
const X_OFFSET = 5;
const Y_OFFSET = 5;
const COMPONENT_HEIGHT = 10;

function getClassForStepValue(stepValue) {
  switch (stepValue) {
    case 'complete':
    case 'error':
      return stepValue;
    default:
      return 'todo';
  }
}

/**
 * @component {kat-statustracker} KatalStatustracker Used at an order or product level to give a visual representation of progress, errors, and pending tasks.
 * @status Production
 * @guideline Do Use to track progress across a linear set of steps
 * @guideline Dont Use for cases where there is a large, non-linear set of steps
 * @example HealthyMix {"steps": ["complete", "complete", "error", "complete", "todo"]}
 */
@register('kat-statustracker')
export class KatalStatustracker extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {json} steps an array of states to display the linear steps, in desired order. Valid states are "complete", "error", or "todo"
       * @required
       */
      steps: { type: Array },
    };
  }

  static get styles() {
    return [baseStyles, styles];
  }

  render() {
    const steps = this.steps || [];
    const numLines = Math.max(steps.length - 1, 0);

    const componentWidth = steps.length
      ? X_OFFSET + numLines * LINE_LENGTH + X_OFFSET
      : 0;

    const lines = numLines
      ? rangeMap(numLines, i => {
          const lineOriginX = X_OFFSET + i * LINE_LENGTH;
          const lineDestinationX = X_OFFSET + (i + 1) * LINE_LENGTH;
          return svg`
            <line
              x1=${lineOriginX}
              y1=${Y_OFFSET}
              x2=${lineDestinationX}
              y2=${Y_OFFSET}
              class="edge ${getClassForStepValue(steps[i + 1])}"
            />
          `;
        })
      : nothing;

    const points = steps.map((step, i) => {
      const stepX = X_OFFSET + i * LINE_LENGTH;
      return svg`
        <circle
          cx="${stepX}"
          cy="${Y_OFFSET}"
          r="${STEP_DOT_RADIUS}"
          class="step ${getClassForStepValue(step)}"
        />
      `;
    });

    return html`
      <svg height="${COMPONENT_HEIGHT}" width="${componentWidth}">
        <g>${lines}</g>
        <g>${points}</g>
      </svg>
    `;
  }
}
